/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {IconButton, IconButtonLabelPlacement} from '../../components/buttons/IconButton';
import {DiscardFiltersIcon} from '../../icons/DiscardFiltersIcon';
import useThemeContext from '../../theme/useThemeContext';
import {IconSize} from '../../types/types';
import Tooltip from '../tooltip/Tooltip';

export const GridClearFiltersTooltip = function(props) {
  return <Tooltip arrow {...props} />;
};

// defines the admin container props ...
export type GridClearFiltersButtonProps = {
  onClearFilters: () => void;
  areFiltersApplied: boolean;
  size?: IconSize;
  disabled?: boolean;
  showCurrentLabel?: boolean;
  currentLabelPlacement?: IconButtonLabelPlacement;
  disableTooltip?: boolean;
};

/**
 * The GridClearFiltersButton provides the Clear Filter Button which will clear the grid API of its filter model.
 *
 * ```typescript
 *   import {GridClearFiltersButton} from '../../components/buttons/';
 *
 *   <GridClearFiltersButton onClearFilters={onClearFilters} />
 * ```
 */
export const GridClearFiltersButton = ({
  onClearFilters,
  areFiltersApplied,
  disabled = false,
  size = IconSize.medium,
  showCurrentLabel = true,
  currentLabelPlacement = IconButtonLabelPlacement.STACKED,
  disableTooltip = false
}: GridClearFiltersButtonProps) => {
  const {isCurrent: isCurrentTheme} = useThemeContext();

  if (areFiltersApplied) {
    const clearFiltersLabel = translate('common:filters.clearFilters');
    const label             = translate('common:columnChooser.clear');
    const button            = (
      <div>
        <IconButton disabled={disabled} icon={<DiscardFiltersIcon size={size} />}
          label={isCurrentTheme ? label : undefined}
          labelPlacement={currentLabelPlacement}
          hideLabel={isCurrentTheme && !showCurrentLabel}
          onClick={onClearFilters} />
      </div>
    );

    if (isCurrentTheme || disableTooltip) {
      return button;
    }

    return (
      <GridClearFiltersTooltip title={clearFiltersLabel}>
        {button}
      </GridClearFiltersTooltip>
    );
  }
  return null;
};

export default GridClearFiltersButton;
